import { useState, useEffect } from "react";
import { Icon, api } from "../lib/core";

export const advertsFromContent = (ad) => {
  if (!ad) return [];
  if (Array.isArray(ad.adverts) && ad.adverts.length > 0)
    return ad.adverts.filter((a) => a && (a.image || a.title || a.text));
  if (ad.advert_image || ad.advert_title || ad.advert_text)
    return [{ image: ad.advert_image, title: ad.advert_title, text: ad.advert_text, link: ad.advert_link, cta: ad.advert_cta }];
  return [];
};

// Auto-rotating advert carousel (admin-managed slides + admin-managed transition interval).
// variant: "panel" (tall right sidebar) | "banner" (full-width inline strip for mobile/≤16").

export const AdvertCarousel = ({ ads, interval, variant = "panel" }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!ads || ads.length <= 1) return;
    const ms = Math.max(2, Number(interval) || 6) * 1000;
    const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), ms);
    return () => clearInterval(t);
  }, [ads, interval]);
  useEffect(() => { if (idx > ads.length - 1) setIdx(0); }, [ads, idx]);

  const a = ads[idx] || {};

  if (variant === "banner") {
    return (
      <div
        className="relative rounded-3xl overflow-hidden border border-slate-200/70 shadow-sm bg-slate-900"
        data-testid="advert-card"
        style={{ height: "220px" }}
      >
        <div key={idx} className="absolute inset-0 animate-fadeIn">
          {a.image ? (
            <img src={a.image} alt={a.title || "Advert"} className={`w-full h-full ${a.fit === "contain" ? "object-contain" : "object-cover"}`} data-testid="advert-image" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-custom to-primary-dark" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5">
            {a.title && <h4 className="font-bold text-white text-lg" data-testid="advert-title">{a.title}</h4>}
            {a.text && <p className="text-sm text-white/80 mt-0.5 line-clamp-2" data-testid="advert-text">{a.text}</p>}
            {a.link && (
              <a href={a.link} target="_blank" rel="noreferrer" data-testid="advert-cta"
                 className="inline-flex items-center gap-1 mt-3 bg-white text-slate-900 hover:bg-slate-100 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
                {a.cta || "Learn more"} <Icon name="arrow_forward" className="text-base" />
              </a>
            )}
          </div>
        </div>
        {ads.length > 1 && (
          <div className="absolute bottom-3 right-4 flex justify-center gap-1.5" data-testid="advert-dots">
            {ads.map((_, i) => (
              <button key={i} type="button" onClick={() => setIdx(i)} data-testid={`advert-dot-${i}`} aria-label={`Show advert ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === idx ? "bg-white w-5" : "bg-white/50 hover:bg-white/80 w-2"}`} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-3xl border border-slate-200/70 overflow-hidden shadow-sm flex flex-col relative"
      data-testid="advert-card"
      style={{ height: "19.00cm", width: "10.22cm" }}
    >
      <div key={idx} className="flex flex-col h-full animate-fadeIn">
        {a.image ? (
          <img src={a.image} alt={a.title || "Advert"} className={`w-full flex-1 min-h-0 ${a.fit === "contain" ? "object-contain" : "object-cover"}`} data-testid="advert-image" />
        ) : (
          <div className="flex-1 min-h-0 bg-gradient-to-br from-primary-custom to-primary-dark" />
        )}
        <div className="p-5 pb-9 space-y-2 flex-shrink-0">
          {a.title && <h4 className="font-bold text-slate-900" data-testid="advert-title">{a.title}</h4>}
          {a.text && <p className="text-sm text-slate-500" data-testid="advert-text">{a.text}</p>}
          {a.link && (
            <a href={a.link} target="_blank" rel="noreferrer" data-testid="advert-cta"
               className="inline-flex items-center gap-1 mt-2 bg-primary-custom hover:bg-primary-dark text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
              {a.cta || "Learn more"} <Icon name="arrow_forward" className="text-base" />
            </a>
          )}
        </div>
      </div>
      {ads.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5" data-testid="advert-dots">
          {ads.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              data-testid={`advert-dot-${i}`}
              aria-label={`Show advert ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === idx ? "bg-primary-custom w-5" : "bg-slate-300 hover:bg-slate-400 w-2"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};


export const AdvertPanel = () => {
  const [ad, setAd] = useState(null);
  useEffect(() => {
    api.get("/content").then((r) => setAd(r.data)).catch(() => {});
  }, []);
  if (!ad) return <aside className="hidden 2xl:block flex-shrink-0" style={{ width: "10.22cm" }} />;

  // Prefer the multi-advert array; fall back to the legacy single-advert fields.
  const ads = advertsFromContent(ad);
  const hasAd = ad.advert_enabled && ads.length > 0;

  return (
    <aside className="hidden 2xl:block flex-shrink-0 self-stretch" data-testid="advert-panel" style={{ width: "10.22cm" }}>
      <div className="sticky top-6 space-y-5">
        {hasAd ? (
          <AdvertCarousel ads={ads} interval={ad.advert_interval} />
        ) : (
          <div className="bg-gradient-to-br from-primary-custom to-primary-dark rounded-3xl p-6 text-white flex flex-col justify-center" data-testid="advert-fallback" style={{ height: "19.00cm", width: "10.22cm" }}>
            <Icon name="campaign" className="text-3xl text-white/90" />
            <h4 className="font-bold mt-2">{ad.promo_banner || "Get rewarded on every top-up"}</h4>
            <p className="text-sm text-white/80 mt-1">Pay from your wallet and earn instant cashback.</p>
          </div>
        )}
        {!hasAd && (
          <div className="bg-white rounded-3xl border border-slate-200/70 p-5">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Sponsored</p>
            <p className="text-sm text-slate-500 mt-1">Ad space managed by the TopIt team.</p>
          </div>
        )}
      </div>
    </aside>
  );
};

// Dashboard Home view
